import { useEffect, useState } from "react";
import api, { formatINR, formatDate } from "../lib/api";
import { Link } from "react-router-dom";

const BUCKETS = ["0-30", "31-60", "61-90", "90+"];
const BUCKET_COLORS = {
  "0-30": "bg-[#EAF2ED] text-[#2D523B]",
  "31-60": "bg-[#FCECD8] text-[#915A1A]",
  "61-90": "bg-[#FAD9D9] text-[#8A2526]",
  "90+": "bg-[#1A1A18] text-white",
};

export default function Aging() {
  const [data, setData] = useState(null);
  const [expand, setExpand] = useState(null);
  const [ledger, setLedger] = useState(null);

  useEffect(() => { api.get("/reports/aging").then(r => setData(r.data)).catch(()=>{}); }, []);

  async function openLedger(name) {
    setExpand(name);
    const { data } = await api.get(`/reports/customer-ledger/${encodeURIComponent(name)}`);
    setLedger(data);
  }

  if (!data) return <div className="text-[#68665E] text-sm" data-testid="aging-loading">Loading aging report...</div>;

  return (
    <div data-testid="aging-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Collections</div>
        <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Aging Report</h1>
        <p className="text-sm text-[#68665E] mt-2">As of <span className="font-mono">{formatDate(data.as_of)}</span> · Total outstanding <span className="font-medium text-[#1A1A18]" data-testid="aging-total">{formatINR(data.total_outstanding)}</span></p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
        {BUCKETS.map((b) => (
          <div key={b} className="card-surface p-5" data-testid={`bucket-${b}`}>
            <div className={`inline-block px-2 py-0.5 text-xs uppercase tracking-wide ${BUCKET_COLORS[b]}`}>{b} days</div>
            <div className="font-headings text-3xl font-bold text-[#1A1A18] mt-4">{formatINR(data.bucket_totals[b])}</div>
            <div className="text-xs text-[#68665E] mt-1">{data.invoices.filter(i => i.bucket === b).length} invoices</div>
          </div>
        ))}
      </div>

      <div className="card-surface mb-8">
        <div className="px-6 py-4 border-b border-[#E5E3DB]"><div className="section-title mb-0">By Customer</div></div>
        <div className="table-wrap border-0">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-th">Customer</th>
                <th className="table-th">Invoices</th>
                <th className="table-th text-right">0-30</th>
                <th className="table-th text-right">31-60</th>
                <th className="table-th text-right">61-90</th>
                <th className="table-th text-right">90+</th>
                <th className="table-th text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {data.by_customer.length === 0 ? (
                <tr><td className="table-td text-[#68665E] text-center" colSpan={7}>Nothing outstanding — well done.</td></tr>
              ) : data.by_customer.map((c) => (
                <tr key={c.customer_name} className="table-row cursor-pointer" onClick={() => openLedger(c.customer_name)} data-testid={`customer-ledger-row-${c.customer_name}`}>
                  <td className="table-td font-medium">{c.customer_name}</td>
                  <td className="table-td">{c.invoice_count}</td>
                  {BUCKETS.map((b) => (
                    <td key={b} className="table-td text-right text-[#68665E]">{c.buckets[b] ? formatINR(c.buckets[b]) : "—"}</td>
                  ))}
                  <td className="table-td text-right font-bold">{formatINR(c.total_outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-surface">
        <div className="px-6 py-4 border-b border-[#E5E3DB]"><div className="section-title mb-0">All Open Invoices</div></div>
        <div className="table-wrap border-0">
          <table className="w-full">
            <thead><tr><th className="table-th">Invoice #</th><th className="table-th">Date</th><th className="table-th">Due</th><th className="table-th">Customer</th><th className="table-th">Bucket</th><th className="table-th">Days</th><th className="table-th text-right">Amount</th></tr></thead>
            <tbody>
              {data.invoices.length === 0 ? (
                <tr><td className="table-td text-[#68665E] text-center" colSpan={7}>No open invoices.</td></tr>
              ) : data.invoices.map((inv) => (
                <tr key={inv.id} className="table-row" data-testid={`aging-invoice-${inv.id}`}>
                  <td className="table-td font-mono text-xs">{inv.invoice_number}</td>
                  <td className="table-td">{formatDate(inv.invoice_date)}</td>
                  <td className="table-td">{formatDate(inv.due_date) || "—"}</td>
                  <td className="table-td">{inv.customer_name}</td>
                  <td className="table-td"><span className={`px-2 py-0.5 text-xs ${BUCKET_COLORS[inv.bucket]}`}>{inv.bucket}</span></td>
                  <td className="table-td">{inv.days_overdue}</td>
                  <td className="table-td text-right font-medium">{formatINR(inv.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-[#E5E3DB] flex justify-end">
          <Link to="/invoices" className="text-xs text-[#C84B31] hover:underline">Go to Invoices →</Link>
        </div>
      </div>

      {expand && ledger && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center py-12 px-4" onClick={() => { setExpand(null); setLedger(null); }} data-testid="ledger-modal">
          <div className="bg-white w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-[#E5E3DB] flex items-center justify-between">
              <h2 className="font-headings text-xl font-bold">{ledger.customer_name}</h2>
              <button className="btn-ghost" onClick={() => { setExpand(null); setLedger(null); }}>Close</button>
            </div>
            <div className="p-6 grid grid-cols-3 gap-4 border-b border-[#E5E3DB]">
              <div><div className="text-xs uppercase tracking-wide text-[#68665E]">Invoiced</div><div className="font-headings text-2xl font-bold mt-1">{formatINR(ledger.summary.total_invoiced)}</div></div>
              <div><div className="text-xs uppercase tracking-wide text-[#68665E]">Paid</div><div className="font-headings text-2xl font-bold mt-1 text-[#2D523B]">{formatINR(ledger.summary.paid)}</div></div>
              <div><div className="text-xs uppercase tracking-wide text-[#68665E]">Outstanding</div><div className="font-headings text-2xl font-bold mt-1 text-[#8A2526]">{formatINR(ledger.summary.outstanding)}</div></div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead><tr><th className="table-th">Invoice #</th><th className="table-th">Date</th><th className="table-th">Status</th><th className="table-th text-right">Total</th></tr></thead>
                <tbody>
                  {ledger.invoices.map((inv) => (
                    <tr key={inv.id} className="table-row">
                      <td className="table-td font-mono text-xs">{inv.invoice_number}</td>
                      <td className="table-td">{formatDate(inv.invoice_date)}</td>
                      <td className="table-td"><span className={inv.status === "paid" ? "badge-success" : inv.status === "overdue" ? "badge-error" : "badge-pending"}>{inv.status}</span></td>
                      <td className="table-td text-right font-medium">{formatINR(inv.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
